import { useContext } from 'solid-js';
import { useLocation } from '@solidjs/router';
import { AppContext } from '../../app-context-provider/app-context-provider';

/* Shared user-account logic for any surface that exposes login / profile actions
   (e.g. UserControls in the desktop header, MenuUtilities in the mobile overlay).
   Encapsulates: logged-in/out state, initials derivation, the open-menu click
   handler, and the visibility rule for the login affordance. Visual rendering
   stays in the consumer — this factory provides state + behaviour only. Must be 
   called during component setup since it reads context. */
export const createUserAccount = () => {
    const { userState, appStore, siteInfo, userProfileUrls } = useContext(AppContext);
    const { close, openLoginMenu } = appStore;
    const location = useLocation();

    const isOnAccountPage = () => {
        const root = userProfileUrls.account;
        return location.pathname === root || location.pathname.startsWith(`${root}/`);
    };

    // Bookmarks are publicly viewable (cookie-backed), so landing on that page must not
    // nudge patients/caregivers to log in — unlike the auth-only account pages.
    const isOnPublicBookmarks = () => location.pathname === userProfileUrls.bookmarks;

    const loggedIn = () => Boolean(userState.user);
    const loggedOut = () => !loggedIn();

    const getInitials = () => {
        const user = userState.user;
        if (!user) return '';

        if (user.firstName && user.lastName) {
            return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
        }

        if (user.firstName) {
            return user.firstName.substring(0, 2).toUpperCase();
        }

        if (user.email) {
            return user.email.substring(0, 2).toUpperCase();
        }

        return 'U';
    };

    const handleClick = () => {
        close();
        openLoginMenu();
    };

    /* Login affordance visibility for logged-out users — preserves the legacy
       UserControls rule:
       - atos-care site: always show
       - default site: only when the user is browsing as HCP (PAC users don't
         see a login button — historical product decision).
       - account pages: always show, so a user landing directly on /my-account
         (without first toggling the HCP ratchet) can still sign in — EXCEPT the
         public bookmarks page, which patients/caregivers may view without login.
        */
    const shouldShowLoginAffordance = () =>
        loggedOut() && (siteInfo.siteType === 'atos-care' || appStore.isHcp() || (isOnAccountPage() && !isOnPublicBookmarks()));

    return {
        loggedIn,
        loggedOut,
        getInitials,
        handleClick,
        shouldShowLoginAffordance,
    };
};
